import React from "react";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Button, Input, Stack, Text,
} from "@chakra-ui/react";
import { useAuthTheme } from "./useAuthTheme";

/**
 * In-page replacements for `window.confirm` / `window.prompt` (POLISH v1, 2026-09-25).
 *
 * The browser's boxes can't be styled, ignore dark mode, read "localhost:3000 says…", and on phones
 * look like a system error. These keep the SAME contract so a swap is one line and cannot change
 * behaviour:
 *
 *   const { confirm, confirmDialog } = useAuthConfirm();
 *   if (!(await confirm({ title: "Delete this contact?", body: "…", confirmLabel: "Delete", tone: "danger" }))) return;
 *   …render {confirmDialog} once anywhere in the component.
 *
 * `confirm` resolves true only on the confirm button; Cancel, Escape and clicking outside all resolve
 * false (exactly like the browser's Cancel). `prompt` resolves the typed text, or null on cancel.
 * Cancel is the least-destructive, focused default.
 */
type ConfirmOpts = {
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "warning" | "primary";
};
type PromptOpts = ConfirmOpts & { label?: string; defaultValue?: string; placeholder?: string; inputType?: string };
type Pending =
  | { kind: "confirm"; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { kind: "prompt"; opts: PromptOpts; resolve: (v: string | null) => void };

export function useAuthConfirm() {
  const t = useAuthTheme();
  const [pending, setPending] = React.useState<Pending | null>(null);
  const [value, setValue] = React.useState("");
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const confirm = React.useCallback(
    (opts: ConfirmOpts) => new Promise<boolean>((resolve) => setPending({ kind: "confirm", opts, resolve })),
    [],
  );
  const prompt = React.useCallback((opts: PromptOpts) => {
    setValue(opts.defaultValue ?? "");
    return new Promise<string | null>((resolve) => setPending({ kind: "prompt", opts, resolve }));
  }, []);

  const close = (result: boolean) => {
    if (!pending) return;
    if (pending.kind === "confirm") pending.resolve(result);
    else pending.resolve(result ? value : null);
    setPending(null);
  };

  const o = pending?.opts;
  const scheme = o?.tone === "danger" ? "red" : o?.tone === "warning" ? "orange" : "blue";
  const confirmDialog = (
    <AlertDialog isOpen={!!pending} leastDestructiveRef={cancelRef} onClose={() => close(false)} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent mx={3} maxW="lg" maxH="calc(100dvh - 48px)" overflowY="auto">
          <AlertDialogHeader fontSize="lg" color={t.text}>{o?.title}</AlertDialogHeader>
          <AlertDialogBody>
            <Stack spacing={3}>
              {o?.body && (typeof o.body === "string" ? <Text fontSize="md" color={t.muted}>{o.body}</Text> : o.body)}
              {pending?.kind === "prompt" && (
                <>
                  {pending.opts.label && <Text as="label" htmlFor="auth-prompt-input" fontSize="md" fontWeight="semibold" color={t.text}>{pending.opts.label}</Text>}
                  <Input
                    id="auth-prompt-input"
                    size="lg"
                    autoFocus
                    type={pending.opts.inputType || "text"}
                    value={value}
                    placeholder={pending.opts.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); close(true); } }}
                  />
                </>
              )}
            </Stack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Stack direction={{ base: "column-reverse", sm: "row" }} spacing={3} w={{ base: "full", sm: "auto" }}>
              <Button ref={cancelRef} size="lg" minH="48px" variant="outline" onClick={() => close(false)}>
                {o?.cancelLabel || "Cancel"}
              </Button>
              <Button size="lg" minH="48px" colorScheme={scheme} onClick={() => close(true)}>
                {o?.confirmLabel || "OK"}
              </Button>
            </Stack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );

  return { confirm, prompt, confirmDialog };
}
